import type { EnrichedOrder, UpdateOrderDeliveryInfo } from '@repo/api-types'
import { queryOptions } from '@tanstack/vue-query'
import { useClient } from '@/composables/useClient'
import { entityKeys } from '@/queries/entity-keys'
import { HttpError } from '@/queries/auth-error'
import { mutationOptions } from '@/queries/mutation-options'

const orderKeys = {
  list: () => [...entityKeys.order()] as const,
  past: () => [...entityKeys.pastOrder()] as const,
}

export const orderQueries = {
  list: () =>
    queryOptions({
      queryKey: orderKeys.list(),
      queryFn: async () => {
        const client = await useClient()
        const { status, body } = await client.order.getOrders()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch orders')
      },
    }),

  past: () =>
    queryOptions({
      queryKey: orderKeys.past(),
      queryFn: async (): Promise<EnrichedOrder[]> => {
        const client = await useClient()
        const { status, body } = await client.order.getPastOrders()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch past orders')
      },
    }),
}

export const orderMutations = {
  addOneOffExtra: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        productVariantId,
        quantity,
      }: {
        orderId: string
        subscriptionId: string
        productVariantId: string
        quantity: number
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.addOneOffExtra({
          params: { orderId },
          body: { subscriptionId, productVariantId, quantity },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to add one-off extra to order')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),

  updateOneOffExtra: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        orderLineItemId,
        quantity,
      }: {
        orderId: string
        subscriptionId: string
        orderLineItemId: string
        quantity: number
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.updateOneOffExtra({
          params: { orderId },
          body: { subscriptionId, orderLineItemId, quantity },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update one-off order extra')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),

  deleteOneOffExtra: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        orderLineItemId,
      }: {
        orderId: string
        subscriptionId: string
        orderLineItemId: string
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.deleteOneOffExtra({
          params: { orderId },
          body: { subscriptionId, orderLineItemId },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to delete one-off order extra')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),

  updateOrderDeliveryInfo: () =>
    mutationOptions({
      mutationFn: async ({ orderId, deliveryInfo }: { orderId: string; deliveryInfo: UpdateOrderDeliveryInfo }) => {
        const client = await useClient()
        const { status, body } = await client.order.updateOrderDeliveryInfo({
          params: { orderId },
          body: deliveryInfo,
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update delivery info')
      },
      invalidation: { background: [{ entity: 'order' }] },
    }),

  retryPayment: () =>
    mutationOptions({
      mutationFn: async ({ orderId }: { orderId: string }) => {
        const client = await useClient()
        const { status, body } = await client.payment.retryPayment({ body: { orderId } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to retry payment')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),
}
